// @ts-strict-ignore
import { Text } from '@backend/lib-design-system';
import { type Dispatch, type SetStateAction, useCallback, useEffect } from 'react';
import { css, styled } from 'styled-components';

import { useTranslation } from '~/hooks';
import { getMediaQuery } from '~/utils/responsive/media';

import type { MenuItem } from './types';

const StyledMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  position: relative;

  ${getMediaQuery('mobile')} {
    gap: 1rem;
  }
`;

const StyledMenuItem = styled.div<{ $isOpen: boolean }>`
  position: static;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 8px 16px;
  color: ${({ theme }) => theme.palette.white};
  font-weight: 700;
  transition:
    color 0.2s,
    border-bottom 0.2s;
  border-bottom: 3px solid transparent;

  &:hover {
    color: ${({ theme }) => theme.colors.accent?.normal};
  }

  ${({ $isOpen, theme }) =>
    $isOpen &&
    css`
      color: ${theme.colors.accent?.normal};
      border-bottom: 3px solid ${theme.colors.accent?.normal};
    `}
`;

const StyledMenuItemText = styled(Text)<{ $isOpen?: boolean }>`
  color: ${({ theme, $isOpen }) => ($isOpen ? theme.colors.accent?.normal : theme.palette.white)} !important;

  ${StyledMenuItem}:hover & {
    color: ${({ theme }) => theme.colors.accent?.normal} !important;
  }
`;

type MenuProps = {
  menuItems: MenuItem[];
  selectedMenuName: string | undefined;
  setSelectedMenuName: Dispatch<SetStateAction<string | undefined>>;
};

export const MenuDesktop = ({ menuItems, selectedMenuName, setSelectedMenuName }: MenuProps) => {
  const t = useTranslation();

  const handleCloseMenu = useCallback(() => {
    setSelectedMenuName(undefined);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      handleCloseMenu();
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <StyledMenu>
      {menuItems.map(({ name }) => {
        const isOpen = selectedMenuName === name;
        return (
          <StyledMenuItem key={name} $isOpen={isOpen} onMouseEnter={() => setSelectedMenuName(name)}>
            <StyledMenuItemText variant="body" as="span" $isOpen={isOpen}>
              {t(`menu.${name}`)}
            </StyledMenuItemText>
          </StyledMenuItem>
        );
      })}
    </StyledMenu>
  );
};
